import React, {useCallback, useMemo, useState} from 'react';
import cx from 'classnames';
import {
    ContextMenu,
    ContextMenuItem,
    Icon,
    PriorityIcon
} from '@priority-software/priority-style-react';
import usePortal from 'react-useportal';
import {isRTL} from '../../../../../Localization';
import {formsStore} from '../../../../../stores/Form/FormsStore';

import {ContextArrayType, ContextMenuTextColor} from '../types';
import {ContextMenuItemContent} from '../components/ContextMenuItemContent';
import {texts} from '../../AdvancedSearch/AdvancedSearchDrawer/texts';
import styles from './styles.module.scss';

type Props = {
    width?: number;
    zIndex?: number;
    menuData: ContextArrayType;
    portalSelector?: string;
};

export const renderSubMenu = (subMenu: ContextArrayType, zIndex?: number) => {
    if (!subMenu) return null;

    return subMenu.map(subItem => {
        const isDisabled = subItem.planning;
        const textColor = subItem.color || ContextMenuTextColor.black;

        return (
            <ContextMenuItem
                onClick={subItem.onClick}
                divider={subItem.divider}
                key={subItem.name}
                isRTL={isRTL()}
                disabled={subItem.planning}
                shortCode={!subItem.planning ? subItem.shortCode : null}
                componentClasses={cx('new-ui-theme', styles.contextItem, {
                    [styles.disabled]: isDisabled
                })}
                icon={
                    <Icon
                        componentClasses={styles.contextIconColor}
                        icon={isRTL() ? PriorityIcon.arrowLeft : PriorityIcon.arrowRight}
                    />
                }
            >
                <ContextMenuItemContent
                    planning={subItem.planning}
                    text={subItem.name}
                    textColor={textColor}
                    tooltip={subItem.planning ? texts.featurePlannedTooltip : null}
                    tooltipZindex={zIndex}
                />
            </ContextMenuItem>
        );
    });
};

export const useContextMenu = ({width = 200, zIndex = 15000, menuData, portalSelector}: Props) => {
    const [position, setPosition] = useState({});
    const [isOpen, setOpen] = useState(false);
    const {Portal} = usePortal({
        ...(portalSelector && {bindTo: document.getElementById(portalSelector)})
    });

    const openContextMenu = useCallback(
        async (anchor: HTMLElement, x: number, y: number) => {
            if (anchor && !isOpen) {
                setPosition({
                    position: 'fixed',
                    top: y,
                    left: x,
                    right: 'auto',
                    width,
                    zIndex
                });
                setOpen(true);
            }
        },
        [width, zIndex, isOpen]
    );

    const closeContextMenu = useCallback(() => {
        if (isOpen) {
            setOpen(false);
            const currentForm = formsStore.getActiveForm();
            currentForm?.formActiveElement?.focus();
        }
    }, [isOpen]);

    const contextMenuComponent = useMemo(() => {
        return (
            <Portal>
                <div className="reactComp">
                    {isOpen && (
                        <div
                            style={{zIndex}}
                            className={styles.contextMenuBackdrop}
                            onClick={closeContextMenu}
                            onContextMenu={closeContextMenu}
                        />
                    )}

                    <ContextMenu
                        isRTL={isRTL()}
                        dropdownStyle={position}
                        isOpen={isOpen}
                        onClose={closeContextMenu}
                        componentClasses={styles.dropdown}
                    >
                        {menuData.map((item, idx) => {
                            return (
                                <ContextMenuItem
                                    onClick={item.onClick}
                                    key={idx}
                                    isRTL={isRTL()}
                                    disabled={item.planning}
                                    shortCode={!item.planning ? item.shortCode : null}
                                    subMenu={renderSubMenu(
                                        item.subMenu?.filter(item => !item.hidden),
                                        zIndex
                                    )}
                                    componentClasses={cx('new-ui-theme', styles.contextItem)}
                                    icon={
                                        <Icon
                                            componentClasses={styles.contextIcon}
                                            icon={
                                                isRTL()
                                                    ? PriorityIcon.arrowLeft
                                                    : PriorityIcon.arrowRight
                                            }
                                        />
                                    }
                                >
                                    <ContextMenuItemContent
                                        planning={item.planning}
                                        text={item.name}
                                        textColor={item.color || ContextMenuTextColor.black}
                                        tooltip={item.planning ? texts.featurePlannedTooltip : null}
                                        tooltipZindex={zIndex}
                                    />
                                </ContextMenuItem>
                            );
                        })}
                    </ContextMenu>
                </div>
            </Portal>
        );
    }, [Portal, isOpen, menuData, position, zIndex, closeContextMenu]);

    return {openContextMenu, closeContextMenu, contextMenuComponent};
};
